// Tracking layer — strictly gated by GDPR consent (Consent Mode v2).
// Scripts are NEVER loaded before explicit consent.

import { getConsent, CONSENT_EVENT } from "./consent";

const GA4_ID = process.env.REACT_APP_GA4_ID || "";
const ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID || "";
const ADS_LEAD_LABEL = process.env.REACT_APP_GOOGLE_ADS_LEAD_LABEL || "";
const ADS_PURCHASE_LABEL = process.env.REACT_APP_GOOGLE_ADS_PURCHASE_LABEL || "";
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || "";
// M911 — Microsoft Clarity (heatmaps + session recordings). Gated on
// `consent.analytics` (NOT marketing) — Clarity is positioned by Microsoft
// as a behavioural-analytics tool, same category as GA4. Empty env var
// disables the feature entirely; nothing in this file looks up Clarity
// until both the env id AND the consent flag are present.
const CLARITY_PROJECT_ID = process.env.REACT_APP_CLARITY_PROJECT_ID || "";

let _consentDefaultsSet = false;
let _ga4Loaded = false;
let _adsLoaded = false;
let _metaLoaded = false;
let _clarityLoaded = false;

function loadScript(src) {
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = resolve;
    s.onerror = resolve;
    document.head.appendChild(s);
  });
}

/** Set Consent Mode v2 defaults to "denied" before any tag loads. Idempotent. */
export function initConsentDefaults() {
  if (typeof window === "undefined" || _consentDefaultsSet) return;
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  window.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
  });
  _consentDefaultsSet = true;
}

/** Apply granted/denied consent for a given category set. Loads scripts as needed. */
export async function applyConsent(consent) {
  if (typeof window === "undefined" || !consent) return;
  const analytics = !!consent.analytics;
  const marketing = !!consent.marketing;

  initConsentDefaults();

  // Notify Google about user choice
  window.gtag("consent", "update", {
    analytics_storage: analytics ? "granted" : "denied",
    ad_storage: marketing ? "granted" : "denied",
    ad_user_data: marketing ? "granted" : "denied",
    ad_personalization: marketing ? "granted" : "denied",
  });

  // GA4 — only on analytics consent
  if (analytics && GA4_ID && !_ga4Loaded) {
    await loadScript(`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`);
    window.gtag("js", new Date());
    // PR-T1 (G4): disable GA4 auto page_view — App.js dispatches an explicit
    // `page_view` on every route change, so leaving auto-collection on would
    // double-count.
    window.gtag("config", GA4_ID, {
      send_page_view: false,
      anonymize_ip: true,
      cookie_flags: "SameSite=None;Secure",
    });
    _ga4Loaded = true;
  }

  // Google Ads — only on marketing consent
  if (marketing && ADS_ID && !_adsLoaded) {
    if (!_ga4Loaded) {
      await loadScript(`https://www.googletagmanager.com/gtag/js?id=${ADS_ID}`);
    }
    window.gtag("config", ADS_ID, { allow_enhanced_conversions: true });
    _adsLoaded = true;
  }

  // Meta Pixel — only on marketing consent
  if (marketing && META_PIXEL_ID && !_metaLoaded) {
    /* eslint-disable */
    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
      if (!f._fbq) f._fbq = n;
      n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
      t = b.createElement(e); t.async = !0; t.src = v;
      s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    /* eslint-enable */
    window.fbq("consent", "grant");
    window.fbq("init", META_PIXEL_ID);
    window.fbq("track", "PageView");
    _metaLoaded = true;
  }

  // M911 + M912 — Microsoft Clarity (heatmaps + session recordings).
  // Gated on `consent.analytics`. Loaded ONCE per page-load via
  // `_clarityLoaded`. Clarity natively tracks SPA navigation via History
  // API hooks, so we don't manually re-init on route change.
  //
  // ROUTE GATE (M912): we do NOT initialise Clarity on the admin dashboard.
  // Admin pages display lead PII (emails, phones, payment ids) we never
  // want in recordings. The check is done at first applyConsent call AND
  // also defensively re-applied on every consent change via the same path.
  const isAdminRoute = typeof window !== "undefined"
    && window.location
    && window.location.pathname
    && window.location.pathname.startsWith("/admin");

  if (analytics && CLARITY_PROJECT_ID && !_clarityLoaded && !isAdminRoute) {
    /* eslint-disable */
    (function (c, l, a, r, i, t, y) {
      // Idempotency: if some other code already bootstrapped Clarity,
      // bail out — re-init causes duplicate sessions.
      if (c[a]) return;
      c[a] = function () { (c[a].q = c[a].q || []).push(arguments); };
      t = l.createElement(r); t.async = 1;
      t.src = "https://www.clarity.ms/tag/" + i;
      y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", CLARITY_PROJECT_ID);
    /* eslint-enable */
    try {
      // M913B — Selective PII masking strategy.
      // Default Clarity behaviour ALREADY masks `<input>`, `<textarea>`,
      // `<select>` VALUES automatically — so anything users TYPE never
      // reaches Clarity recordings, regardless of `maskText` mode.
      // For visible TEXT, we use `balanced` (Clarity's default) which
      // keeps generic copy visible but masks anything that looks like
      // PII (emails, phone numbers, numeric blobs > 4 digits).
      // On top of that, we explicitly add the `.clarity-mask` class on:
      //   • PII INPUT elements (LeadForm + Checkout + Webinar form:
      //     prenume, nume, email, telefon, school name)
      //   • DISPLAYED PII values (Success page Field `<dd>` for order_id,
      //     Checkout PaymentOnly SummaryRow values, Admin Dashboard)
      // Labels, dropdown options, checkboxes, validation, CTAs and the
      // entire public marketing copy stay VISIBLE — Clarity remains
      // useful for UX & abandonment analysis.
      //
      // Reference: https://learn.microsoft.com/clarity/setup-and-installation/clarity-masking
      window.clarity("set", "maskText", "balanced");
      // Consent signal — see https://learn.microsoft.com/clarity/setup-and-installation/cookie-consent
      window.clarity("consent");
    } catch { /* ignore — script may still be loading */ }
    _clarityLoaded = true;
  }
}

/** Initialize consent defaults at app boot. Apply stored consent if any. */
export function bootstrapTracking() {
  initConsentDefaults();
  const stored = getConsent();
  if (stored) applyConsent(stored);

  // React to consent changes from anywhere in the app
  if (typeof window !== "undefined") {
    window.addEventListener(CONSENT_EVENT, (e) => {
      if (e.detail) applyConsent(e.detail);
    });
  }
}

// ---------- Event helpers (no-op if scripts not yet loaded due to no consent) ----------

function gtagSafe(...args) {
  if (typeof window === "undefined" || !window.gtag) return;
  try { window.gtag(...args); } catch { /* noop */ }
}
function fbqSafe(...args) {
  if (typeof window === "undefined" || !window.fbq) return;
  try { window.fbq(...args); } catch { /* noop */ }
}

export function trackEvent(name, params = {}) {
  // Pushed to dataLayer; GA4 will only forward if analytics_storage=granted
  gtagSafe("event", name, params);
}

export function trackMeta(eventName, params = {}, custom = false, options = undefined) {
  if (custom) fbqSafe("trackCustom", eventName, params, options);
  else fbqSafe("track", eventName, params, options);
}

export function trackPageView(path) {
  trackEvent("page_view", { page_path: path });
  trackMeta("PageView");
}

export function trackLead(lead, value = 0, currency = "RON", eventId = undefined) {
  // PR-T3: GA4 + Meta browser-side dedup key (same uuid sent to backend).
  const dedupe = eventId || lead?.event_id || undefined;
  trackEvent("generate_lead", {
    value,
    currency,
    source: lead?.source || "lead_form",
    ...(dedupe ? { dedupe_id: dedupe, transaction_id: dedupe } : {}),
  });
  trackEvent("form_submit", { form_id: lead?.source || "lead_form" });
  trackMeta("Lead", { value, currency }, false, dedupe ? { eventID: dedupe } : undefined);
  if (ADS_ID && ADS_LEAD_LABEL) {
    trackEvent("conversion", {
      send_to: `${ADS_ID}/${ADS_LEAD_LABEL}`,
      value,
      currency,
      user_data: { email_address: (lead?.email || "").toLowerCase() },
    });
  }
}

export function trackBeginCheckout(value, currency = "RON") {
  trackEvent("begin_checkout", { value, currency });
  trackMeta("InitiateCheckout", { value, currency });
}

export function trackAddPaymentInfo(value, currency = "RON") {
  trackEvent("add_payment_info", { value, currency });
  trackMeta("AddPaymentInfo", { value, currency });
}

export function trackPurchase(purchase) {
  const value = purchase?.amount || 0;
  const currency = purchase?.currency || "RON";
  const txnId = purchase?.id;
  // `transaction_id` on GA4 and `eventID` on Meta Pixel act as dedupe keys:
  // when we later add server-side CAPI / Measurement Protocol, identical IDs
  // will be deduplicated automatically.
  trackEvent("purchase", { transaction_id: txnId, value, currency });
  trackMeta("Purchase", { value, currency }, false, txnId ? { eventID: txnId } : undefined);
  if (ADS_ID && ADS_PURCHASE_LABEL) {
    trackEvent("conversion", {
      send_to: `${ADS_ID}/${ADS_PURCHASE_LABEL}`,
      transaction_id: txnId,
      value,
      currency,
      user_data: { email_address: (purchase?.email || "").toLowerCase() },
    });
  }
}

export function trackViewContent(name = "course") {
  trackEvent("view_program", { content_name: name });
  trackMeta("ViewContent", { content_name: name });
}

export function trackViewPrice(value, currency = "RON") {
  trackEvent("view_price", { value, currency });
}

export function trackClickWhatsapp() {
  trackEvent("click_whatsapp");
  trackMeta("Contact", {}, true);
}

export function trackClickEmail() {
  trackEvent("click_email");
  trackMeta("Contact", {}, true);
}

export function trackScroll(depth) {
  trackEvent(`scroll_${depth}`, { depth });
}

// Registration-only flow events (NEVER fire `purchase` for these).
// PR-T1 (G6): Meta event is `CompleteRegistration` (standard) — NOT a custom
// "Lead" — so the funnel doesn't double-count Leads. Backend CAPI also
// dispatches `CompleteRegistration`, deduped via `eventID` (same uuid).
// PR-T1.1 (fix): always send `value` + `currency` so Pixel JS reliably
// serialises the options object (when params is `{}`, certain Pixel JS
// builds drop the options on the floor → eventID never reaches Meta →
// browser fire arrives without dedup key → events appear server-only).
export function trackRegistrationSubmit(enrollment = null, eventId = undefined, value = 0, currency = "RON") {
  const dedupe = eventId || enrollment?.event_id || undefined;
  trackEvent("complete_registration", {
    value, currency,
    ...(dedupe ? { dedupe_id: dedupe, transaction_id: dedupe } : {}),
  });
  trackMeta(
    "CompleteRegistration",
    { value, currency },
    false,
    dedupe ? { eventID: dedupe } : undefined,
  );
}

export function trackRegistrationSuccess(purchaseId) {
  trackEvent("registration_success", { transaction_id: purchaseId });
}

export function trackPaymentFailed(reason = "") {
  trackEvent("payment_failed", { reason });
}

export function trackPaymentCancelled() {
  trackEvent("payment_cancelled");
}

// ---- Webinarii (Phase 1) ----
// Naming convention follows the rest of the file: snake_case GA4 events.
// Meta: webinar signups fire a CUSTOM event `WebinarRegistration` — NOT a
// standard event — so they stay clearly separated from course pre-registrations
// (`CompleteRegistration`) and lead-form leads (`Lead`) in Meta Events Manager.
// To use it as a campaign objective, create a Custom Conversion on
// `WebinarRegistration`. The custom name is intentionally suggestive so it
// mirrors the GA4 event `webinar_registration_success`.
export function trackWebinarsPageView() {
  trackEvent("view_webinars_page");
  trackMeta("ViewContent", { content_type: "webinars_page" });
}

export function trackWebinarCardView(webinar) {
  trackEvent("view_webinar_card", {
    webinar_id: webinar?.id,
    webinar_title: webinar?.title,
    webinar_date: webinar?.date,
  });
}

export function trackWebinarRegistrationStart(webinar) {
  trackEvent("webinar_registration_start", {
    webinar_id: webinar?.id,
    webinar_title: webinar?.title,
  });
}

export function trackWebinarRegistrationSubmit(webinar) {
  trackEvent("webinar_registration_submit", {
    webinar_id: webinar?.id,
    webinar_title: webinar?.title,
  });
}

export function trackWebinarRegistrationSuccess(webinar, registrationId) {
  trackEvent("webinar_registration_success", {
    webinar_id: webinar?.id,
    webinar_title: webinar?.title,
    transaction_id: registrationId,
  });
  trackMeta(
    "WebinarRegistration",
    { content_type: "webinar", content_name: webinar?.title || "" },
    true, // custom event — keeps webinar signups distinct from CompleteRegistration
    registrationId ? { eventID: registrationId } : undefined,
  );
}

export function trackWebinarRegistrationError(webinar, errorCode) {
  trackEvent("webinar_registration_error", {
    webinar_id: webinar?.id,
    error_code: errorCode,
  });
}

// ---- Homepage cohort schedule ----
// GA4 only, no Meta counterpart: these measure which section of the homepage
// sends people into checkout, not a conversion. The actual conversion still
// fires once, from `/checkout` (`begin_checkout`) — sending a Meta event here
// too would double-count the same person.
//
// `cohort_schedule` as `source` is what separates these clicks from the price
// card's CTA, which lands on the same page.

/**
 * Someone left the schedule for checkout. `group` is null when the CTA was
 * the general one — the mode where checkout does not ask for a cohort.
 */
export function trackScheduleMonthFilter(value, count) {
  trackEvent("filter_cohorts", {
    source: "cohort_schedule",
    month: value || "all",
    cohorts_shown: typeof count === "number" ? count : null,
  });
}


export function trackScheduleEnroll(group) {
  trackEvent("select_cohort", {
    source: "cohort_schedule",
    group_id: group?.id || null,
    group_name: group?.name || null,
    group_start_date: group?.start_date || null,
    spots_left: typeof group?.spots_left === "number" ? group.spots_left : null,
  });
}

/**
 * Someone asked to be told about future cohorts instead of enrolling now.
 * `reason` says which situation produced the click — `no_fit` (cohorts were
 * open, none suited), `all_full`, or `no_cohorts` — so the three are not
 * read as one number: the first is a scheduling signal, the other two are
 * demand we could not serve.
 */
export function trackScheduleNotify(reason) {
  trackEvent("cohort_notify_request", { source: "cohort_schedule", reason });
}
